import { forwardRef, type InputHTMLAttributes, type Ref, type ReactNode } from 'react';
import { InputWrapper, type InputWrapperBaseProps } from '../shared/InputBase.js';

export interface TextInputProps
  extends InputWrapperBaseProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'color'> {
  /** Render `<input type=...>` */
  /**
   * Native input type. `date`, `time` and `datetime-local` are included
   * because scheduling UIs need them and there is no separate date component
   * in this package; `number` is deliberately absent, since `NumberInput`
   * handles clamping and parsing properly.
   */
  type?: 'text' | 'email' | 'url' | 'tel' | 'search' | 'date' | 'time' | 'datetime-local';
}

export const TextInput = forwardRef(function TextInput(
  props: TextInputProps,
  ref: Ref<HTMLInputElement>
): ReactNode {
  const {
    label, description, error, required, withAsterisk,
    leftSection, rightSection, leftSectionWidth, rightSectionWidth,
    size, className, style,
    id, type = 'text', ...inputProps
  } = props;
  return (
    <InputWrapper
      label={label}
      description={description}
      error={error}
      required={required}
      withAsterisk={withAsterisk}
      leftSection={leftSection}
      rightSection={rightSection}
      leftSectionWidth={leftSectionWidth}
      rightSectionWidth={rightSectionWidth}
      size={size}
      className={className}
      style={style}
      inputId={id}
    >
      {({ inputId, describedBy, invalid, className: ctlClass }) => (
        <input
          ref={ref}
          id={inputId}
          type={type}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={ctlClass}
          {...inputProps}
        />
      )}
    </InputWrapper>
  );
});
